import { branchMeta, raisedMeta, useMultiBranch } from "@/lib/branchInfo";
import { useEffect, useState } from "react";
// doc-design-pr: standard card-list design
import { PageFrame, DocCard, ItemsPanel, DocMenu, useDocFlow, LinkProjectDialog, timeOf } from "@/components/procurement/DocKit";
import { FileText, GitBranch, FolderInput, Ban, Search as SearchIcon, Link2 as LinkIcon, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, ArrowRightCircle, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { DeleteDocButton } from "@/components/procurement/DeleteDocButton";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { Link2 } from "lucide-react";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
import { DraftProcurementEditor } from "@/components/procurement/DraftProcurementEditor";
const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", estimated_rate: "", wbs_item_id: "" };


function useSearch() {
  const [q, setQ] = useState("");
  return [q, setQ, (arr, text) => { const t = q.trim().toLowerCase(); return t ? arr.filter((r) => text(r).toLowerCase().includes(t)) : arr; }];
}
function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative w-full max-w-sm">
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10 rounded-lg pl-9" data-testid={testId} />
    </div>
  );
}
const viewBtn = "h-10 rounded-lg px-6 font-semibold";


export default function PurchaseRequisitions() {
  const multi = useMultiBranch();
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const { companies, singleCompany } = useCompany() || {};
  const [requisitions, setRequisitions] = useState([]);
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [materialsByWbsId, setMaterialsByWbsId] = useState({});
  const [q, setQ, filt] = useSearch();
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, indentRes] = await Promise.all([
        api.get("/procurement/requisitions"), api.get("/procurement/indents").catch(() => ({ data: [] })),
      ]);
      setRequisitions([...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setIndents(indentRes.data);
      const projectIds = [...new Set(data.map((r) => r.project_id).filter(Boolean))];
      const lookups = await Promise.all(projectIds.map((pid) =>
        api.get(`/projects/${pid}/wbs`).then(({ data: wbs }) => wbs.map((w) => [w.id, `${w.code} — ${w.name}`])).catch(() => [])
      ));
      setMaterialsByWbsId(Object.fromEntries(lookups.flat()));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const { openFlow, flowNode } = useDocFlow(load);

  const post = async (r, path, ok) => {
    setBusyId(r.id);
    try { await api.post(`/procurement/requisitions/${r.id}/${path}`, path === "convert-to-rfq" ? { rfq_date: new Date().toISOString().slice(0, 10) } : undefined); toast.success(ok); load(); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setBusyId(null); }
  };
  const remove = async (r) => {
    if (!await confirmAction(`Mark requisition ${r.number} as deleted? It will remain in the procurement history.`)) return;
    try { await api.delete(`/procurement/requisitions/${r.id}`); toast.success(`${r.number} marked deleted`); load(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "";
  const rows = filt(pf.apply(requisitions, (r) => r.project_id), (r) => `${r.number} ${companyName(r.company_id)} ${(r.items || []).map((i) => i.description).join(" ")}`);

  return (
    <PageFrame testId="purchase-requisitions-page" crumbs={[{ label: "Procurement" }, { label: "Purchase Requisitions" }]} title="Purchase Requisitions"
      description="Formal, reviewed requisitions — converted from an approved indent, or created directly."
      actions={<><ProjectFilter filter={pf} />{canWrite && <CreateRequisitionDialog pfProjects={pf.projects} companies={companies} onCreated={load} />}</>}>
      <SearchBox value={q} onChange={setQ} placeholder="Search number, company or item…" testId="requisition-search" />
      {!loading && rows.length === 0 ? (
        <EmptyState title={requisitions.length ? "No requisitions match" : "No purchase requisitions yet"} testId="requisitions-empty-state" />
      ) : (
        <div className="space-y-3">
          {rows.map((r, idx) => {
            const open = expanded[r.id] ?? (idx === 0 && !q);
            const items = r.items || [];
            const fromIndent = r.indent_id ? indents.find((i) => i.id === r.indent_id)?.number : null;
            return (
              <DocCard key={r.id} testId={`requisition-card-${r.number}`} toggleId={`requisition-toggle-${r.number}`} open={open} onToggle={() => setExpanded((e) => ({ ...e, [r.id]: !open }))}
                number={r.number} date={r.requisition_date} time={timeOf(r.created_at)} meta={[...branchMeta(r, multi), ...raisedMeta(r, singleCompany), `${items.length} item${items.length === 1 ? "" : "s"}`]}
                line={companyName(r.company_id) ? <span className="text-muted-foreground">{companyName(r.company_id)}</span> : null}
                note={r.indent_id ? `From indent ${fromIndent || "—"}` : null}
                right={<>
                  <StatusPill value={r.status} />
                  {canWrite && r.status === "DRAFT" && <Button size="sm" variant="outline" className="h-10 rounded-lg" disabled={busyId === r.id} onClick={() => post(r, "submit", "Requisition submitted")} data-testid={`submit-requisition-button-${r.number}`}><Send className="h-3.5 w-3.5" /> Submit</Button>}
                  {canWrite && r.status === "APPROVED" && <Button size="sm" className="h-10 rounded-lg" disabled={busyId === r.id} onClick={() => post(r, "convert-to-rfq", "Converted to RFQ")} data-testid={`convert-requisition-button-${r.number}`}><ArrowRightCircle className="h-3.5 w-3.5" /> Convert to RFQ</Button>}
                  <ApprovalReviewButton docType="PR" docId={r.id} onChanged={load} icon={FileText} className={viewBtn} />
                  <DocMenu testId={`requisition-menu-${r.number}`} items={[
                    { key: "flow", label: "Document flow", icon: GitBranch, onSelect: () => openFlow("PURCHASE_REQUISITION", r.id) },
                    canWrite && r.status === "DRAFT" && { key: "edit", label: "Edit draft", icon: Pencil, onSelect: () => setEditing(r), testId: `requisition-edit-${r.number}` },
                    canWrite && ["DRAFT", "REJECTED", "CANCELLED"].includes(r.status) && { key: "del", label: "Mark deleted", icon: Trash2, danger: true, onSelect: () => remove(r), testId: `requisition-delete-${r.number}` },
                  ]} />
                </>}>
                <ItemsPanel testId={`requisition-items-${r.number}`}
                  columns={[{ label: "Item", w: "3fr" }, { label: "Qty", w: "1fr", right: true }, { label: "Est. rate", w: "1fr", right: true }, { label: "Linked activity", w: "2.5fr" }]}
                  rows={items.map((it) => [
                    it.description, Number(it.quantity), it.estimated_rate ? `₹${it.estimated_rate}` : "—",
                    it.wbs_item_id ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-400" data-testid={`requisition-item-material-link-${it.id}`}><LinkIcon className="h-3 w-3" /> {materialsByWbsId[it.wbs_item_id] || "linked material"}</span> : <span className="text-muted-foreground">—</span>,
                  ])} />
              </DocCard>
            );
          })}
        </div>
      )}
      {flowNode}
      <DraftProcurementEditor phase="requisition" document={editing} projects={pf.projects} onClose={() => setEditing(null)} onSaved={load} />
    </PageFrame>
  );
}

function CreateRequisitionDialog({ companies, pfProjects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", requisition_date: "", remarks: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, estimated_rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.estimated_rate,
  } : it)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/requisitions", { ...form, project_id: form.project_id || null, items: items.map((it) => ({ ...it, estimated_rate: it.estimated_rate || null, wbs_item_id: it.wbs_item_id || null })) });
      toast.success("Purchase requisition created");
      setForm({ company_id: "", requisition_date: "", remarks: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-requisition-button"><Plus className="h-4 w-4" /> New Requisition</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Purchase Requisition</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-requisition-form">
          <div>
            <Label>Project</Label>
            <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="requisitions-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(pfProjects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="requisition-company-select" />
            <div><Label>Date</Label><Input required type="date" data-testid="requisition-date-input" value={form.requisition_date} onChange={set("requisition_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`requisition-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`requisition-item-desc-input-${idx}`} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`requisition-item-qty-input-${idx}`} />
                    {items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-requisition-item-button-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">WBS activity</span>
                    <div className="max-w-xs flex-1"><WbsItemSelect projectId={form.project_id} value={it.wbs_item_id} onChange={(v) => updateItem(idx, "wbs_item_id", v)} testId={`requisition-item-wbs-select-${idx}`} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-requisition-item-button">
              <Plus className="h-3.5 w-3.5" /> Add item
            </Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-requisition-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
